import React, { useEffect, useState } from 'react';
import {
  getConges, approuverConge, rejeterConge,
  getFeuilles, updateFeuille,
  validerFeuilleCom, rejeterFeuilleCom,   // §3.4 — niveau 3
} from '../services/api';
import { useAuth } from '../hooks/useAuth';

function Toast({ msg, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3500); return () => clearTimeout(t); }, [onClose]);
  return <div className={`toast toast-${type}`}>{msg}</div>;
}

const MOIS = ['','Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];

// ── Modal remarque pour rejet/validation comptable ────────────
function RemarqueModal({ titre, onConfirm, onCancel }) {
  const [remarque, setRemarque] = useState('');
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: '#fff', borderRadius: 10, width: 420, boxShadow: '0 20px 60px rgba(0,0,0,.2)',
        padding: 24,
      }}>
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 14, color: '#0f2644' }}>{titre}</div>
        <div className="fg" style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 4, display: 'block' }}>
            Remarque (optionnelle)
          </label>
          <textarea
            value={remarque}
            onChange={e => setRemarque(e.target.value)}
            placeholder="Commentaire ou motif…"
            style={{
              width: '100%', minHeight: 80, padding: '8px 10px',
              border: '1.5px solid #c8d4e0', borderRadius: 6, fontSize: 13,
              fontFamily: 'inherit', resize: 'vertical',
            }}
          />
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-gray" onClick={onCancel}>Annuler</button>
          <button className="btn btn-green" onClick={() => onConfirm(remarque)}>Confirmer</button>
        </div>
      </div>
    </div>
  );
}


// ── Hook tri tableau ──────────────────────────────────────────
function useSortTable(data, defaultCol = '') {
  const [sortCol, setSortCol] = React.useState(defaultCol);
  const [sortDir, setSortDir] = React.useState('asc');
  const handleSort = col => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };
  const sorted = React.useMemo(() => {
    if (!sortCol || !data) return data || [];
    return [...data].sort((a, b) => {
      const va = (a[sortCol] ?? '').toString();
      const vb = (b[sortCol] ?? '').toString();
      const cmp = va.localeCompare(vb, 'fr', { sensitivity: 'base' });
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [data, sortCol, sortDir]);
  const Th = ({ col, label, style = {}, className = '' }) => (
    <th onClick={() => handleSort(col)}
        className={className}
        style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', ...style }}
        title={`Trier par ${label}`}>
      {label}{' '}
      {sortCol === col
        ? (sortDir === 'asc' ? '▲' : '▼')
        : <span style={{ opacity: 0.25 }}>⇅</span>}
    </th>
  );
  return { sorted, Th, sortCol, sortDir };
}

export default function ApprobationsPage() {
  const { user } = useAuth();

  // Droits
  const isRH     = ['admin', 'rh', 'direction'].includes(user?.role);
  const isCompta  = ['admin', 'comptabilite', 'direction'].includes(user?.role);

  const [conges,         setConges]         = useState([]);
  const [feuillesRH,     setFeuillesRH]     = useState([]);  // statut 'Soumis'   → RH valide
  const [feuillesCompta, setFeuillesCompta] = useState([]);  // statut 'Validé'   → Compta valide
  const { sorted: sortedConges,  Th: ThA1 } = useSortTable(conges);
  const { sorted: sortedFeuillesRH,    Th: ThA2 } = useSortTable(feuillesRH);
  const { sorted: sortedFeuillesCompta, Th: ThA3 } = useSortTable(feuillesCompta);
  const [loading,        setLoading]        = useState(true);
  const [subTab,         setSubTab]         = useState('conges');
  const [toast,          setToast]          = useState(null);
  const [modalCompta,    setModalCompta]    = useState(null); // { id, action: 'valider'|'rejeter' }

  const showToast = (msg, type = 'success') => setToast({ msg, type });

  const loadAll = () => {
    setLoading(true);
    Promise.all([
      getConges({ statut: 'En attente' }).then(r => setConges(r.data || [])).catch(() => {}),
      getFeuilles({}).then(r => {
        const all = r.data || [];
        setFeuillesRH(all.filter(f => f.statut === 'Soumis'));
        setFeuillesCompta(all.filter(f => f.statut === 'Validé'));
      }).catch(() => {}),
    ]).finally(() => setLoading(false));
  };

  useEffect(() => { loadAll(); }, []);

  const totalPending = conges.length + feuillesRH.length + feuillesCompta.length;

  // ── Actions ───────────────────────────────────────────────
  const handleValiderRH = (fId) =>
    updateFeuille(fId, { statut: 'Validé' })
      .then(loadAll)
      .then(() => showToast('Feuille validée par le RH ✓'));

  const handleValiderCompta = async (fId, remarque) => {
    try {
      await validerFeuilleCom(fId, { remarque_compta: remarque });
      loadAll();
      showToast('Feuille validée par la comptabilité ✓');
    } catch (err) {
      showToast(err.response?.data?.error || 'Erreur validation comptable', 'error');
    }
  };

  const handleRejeterCompta = async (fId, remarque) => {
    try {
      await rejeterFeuilleCom(fId, { remarque_compta: remarque });
      loadAll();
      showToast('Feuille rejetée par la comptabilité');
    } catch (err) {
      showToast(err.response?.data?.error || 'Erreur rejet comptable', 'error');
    }
  };

  return (
    <>
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}

      {/* Modal remarque comptable */}
      {modalCompta && (
        <RemarqueModal
          titre={modalCompta.action === 'valider'
            ? '✅ Valider la feuille (Comptabilité)'
            : '❌ Rejeter la feuille (Comptabilité)'}
          onCancel={() => setModalCompta(null)}
          onConfirm={(rem) => {
            setModalCompta(null);
            if (modalCompta.action === 'valider') handleValiderCompta(modalCompta.id, rem);
            else handleRejeterCompta(modalCompta.id, rem);
          }}
        />
      )}

      <div className="page-header">
        <div>
          <h2>Approbations</h2>
          <p>{totalPending} élément(s) en attente de validation</p>
        </div>
      </div>

      {totalPending === 0 && !loading && (
        <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
          <div style={{ fontSize: 56, marginBottom: 12 }}>✅</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#2E7D32' }}>Tout est à jour !</div>
          <div style={{ color: '#888', fontSize: 13, marginTop: 6 }}>Aucune demande en attente d'approbation.</div>
        </div>
      )}

      <div className="sub-tabs">
        {/* Onglet Congés */}
        <button
          className={`sub-tab${subTab === 'conges' ? ' active' : ''}`}
          onClick={() => setSubTab('conges')}
        >
          Congés en attente
          {conges.length > 0 && (
            <span className="nav-badge" style={{ marginLeft: 6 }}>{conges.length}</span>
          )}
        </button>

        {/* Onglet RH — feuilles Soumises */}
        {isRH && (
          <button
            className={`sub-tab${subTab === 'feuilles-rh' ? ' active' : ''}`}
            onClick={() => setSubTab('feuilles-rh')}
          >
            Feuilles — validation RH
            {feuillesRH.length > 0 && (
              <span className="nav-badge" style={{ marginLeft: 6, background: '#1565C0' }}>
                {feuillesRH.length}
              </span>
            )}
          </button>
        )}

        {/* Onglet Comptabilité — feuilles Validées RH en attente compta */}
        {isCompta && (
          <button
            className={`sub-tab${subTab === 'feuilles-compta' ? ' active' : ''}`}
            onClick={() => setSubTab('feuilles-compta')}
          >
            Feuilles — validation Comptabilité
            {feuillesCompta.length > 0 && (
              <span className="nav-badge" style={{ marginLeft: 6, background: '#E65100' }}>
                {feuillesCompta.length}
              </span>
            )}
          </button>
        )}
      </div>

      {loading
        ? <div className="empty"><span className="spinner spinner-dark" /></div>
        : (
          <>
            {/* ── CONGÉS ── */}
            {subTab === 'conges' && (
              <div className="card">
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <ThA1 col="nom"          label="Employé(e)" />
                        <ThA1 col="type_absence" label="Type d'absence" />
                        <ThA1 col="date_debut"   label="Du" />
                        <ThA1 col="date_fin"     label="Au" />
                        <th>Jours</th>
                        <th>Motif</th>
                        <ThA1 col="created_at"   label="Soumis le" />
                        <th style={{ textAlign: 'center' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {conges.length === 0
                        ? <tr><td colSpan={8} className="empty">Aucun congé en attente</td></tr>
                        : sortedConges.map(c => (
                          <tr key={c.id}>
                            <td>
                              <div className="flex-c">
                                <div className="avatar" style={{ width: 28, height: 28, fontSize: 11 }}>
                                  {c.prenoms?.[0]}{c.nom?.[0]}
                                </div>
                                <div>
                                  <div style={{ fontWeight: 700 }}>{c.prenoms} {c.nom}</div>
                                  <div style={{ fontSize: 11, color: '#888' }}>{c.matricule}</div>
                                </div>
                              </div>
                            </td>
                            <td style={{ fontSize: 12 }}>{c.type_absence}</td>
                            <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{c.date_debut}</td>
                            <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{c.date_fin}</td>
                            <td style={{ textAlign: 'center', fontWeight: 700 }}>{c.nombre_jours}</td>
                            <td style={{ fontSize: 12, color: '#555', maxWidth: 160 }}>{c.raison || '—'}</td>
                            <td style={{ fontSize: 11, color: '#888' }}>{c.date_soumission?.slice(0, 10)}</td>
                            <td>
                              <div className="act-cell">
                                <button
                                  className="btn btn-green btn-sm"
                                  onClick={() => approuverConge(c.id).then(loadAll).then(() => showToast('Congé approuvé ✓'))}
                                >
                                  ✓ Approuver
                                </button>
                                <button
                                  className="btn btn-red btn-sm"
                                  onClick={() => rejeterConge(c.id).then(loadAll).then(() => showToast('Congé rejeté'))}
                                >
                                  ✗ Rejeter
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ── FEUILLES — NIVEAU 2 : RH ── */}
            {subTab === 'feuilles-rh' && isRH && (
              <div className="card">
                <div style={{
                  background: '#E3F2FD', border: '1px solid #90CAF9',
                  borderRadius: 6, padding: '8px 14px', marginBottom: 12,
                  fontSize: 12, color: '#1565C0',
                }}>
                  📋 <strong>Niveau 2 — Validation RH</strong> : Ces feuilles ont été soumises par les employés et attendent votre validation.
                </div>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <ThA2 col="nom"         label="Employé(e)" />
                        <ThA2 col="mois"        label="Période" />
                        <ThA2 col="superviseur" label="Superviseur" />
                        <th>Commentaire</th>
                        <th style={{ textAlign: 'center' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {feuillesRH.length === 0
                        ? <tr><td colSpan={5} className="empty">Aucune feuille soumise en attente RH</td></tr>
                        : sortedFeuillesRH.map(f => (
                          <tr key={f.id}>
                            <td>
                              <div style={{ fontWeight: 700 }}>{f.prenoms} {f.nom}</div>
                              <div style={{ fontSize: 11, color: '#888' }}>{f.matricule}</div>
                            </td>
                            <td style={{ fontWeight: 700 }}>{MOIS[f.mois]} {f.annee}</td>
                            <td style={{ fontSize: 12 }}>{f.superviseur || '—'}</td>
                            <td style={{ fontSize: 12, color: '#555', maxWidth: 200 }}>{f.commentaire || '—'}</td>
                            <td>
                              <div className="act-cell">
                                <button
                                  className="btn btn-green btn-sm"
                                  onClick={() => handleValiderRH(f.id)}
                                >
                                  ✓ Valider (RH)
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ── FEUILLES — NIVEAU 3 : COMPTABILITÉ ── */}
            {subTab === 'feuilles-compta' && isCompta && (
              <div className="card">
                <div style={{
                  background: '#FFF3E0', border: '1px solid #FFCC80',
                  borderRadius: 6, padding: '8px 14px', marginBottom: 12,
                  fontSize: 12, color: '#E65100',
                }}>
                  🏦 <strong>Niveau 3 — Validation Comptabilité</strong> : Ces feuilles ont été validées par le RH et attendent votre validation finale.
                </div>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <ThA3 col="nom"         label="Employé(e)" />
                        <ThA3 col="mois"        label="Période" />
                        <ThA3 col="superviseur" label="Superviseur" />
                        <th>Commentaire</th>
                        <th style={{ textAlign: 'center' }}>Actions comptabilité</th>
                      </tr>
                    </thead>
                    <tbody>
                      {feuillesCompta.length === 0
                        ? <tr><td colSpan={5} className="empty">Aucune feuille en attente de validation comptable</td></tr>
                        : sortedFeuillesCompta.map(f => (
                          <tr key={f.id}>
                            <td>
                              <div style={{ fontWeight: 700 }}>{f.prenoms} {f.nom}</div>
                              <div style={{ fontSize: 11, color: '#888' }}>{f.matricule}</div>
                            </td>
                            <td style={{ fontWeight: 700 }}>{MOIS[f.mois]} {f.annee}</td>
                            <td style={{ fontSize: 12 }}>{f.superviseur || '—'}</td>
                            <td style={{ fontSize: 12, color: '#555', maxWidth: 200 }}>{f.commentaire || '—'}</td>
                            <td>
                              <div className="act-cell">
                                <button
                                  className="btn btn-green btn-sm"
                                  onClick={() => setModalCompta({ id: f.id, action: 'valider' })}
                                >
                                  ✓ Valider (Compta)
                                </button>
                                <button
                                  className="btn btn-red btn-sm"
                                  onClick={() => setModalCompta({ id: f.id, action: 'rejeter' })}
                                >
                                  ✗ Rejeter
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
    </>
  );
}
